import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../lib/api.js";
import type { RelationshipEventView, RelationshipKind, RelationshipsView } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { Modal } from "../components/Modal.js";
import { CharacterAvatar } from "../components/CharacterAvatar.js";
import { useToast } from "../components/Toast.js";
import { useModelPicker } from "../ai/ModelPicker.js";

const KINDS: RelationshipKind[] = ["romantic", "friend", "family", "professional", "business", "living", "rival", "other"];
const KIND_LABEL: Record<RelationshipKind, string> = {
  romantic: "Romantic", friend: "Friendship", family: "Family", professional: "Professional",
  business: "Business", living: "Living arrangement", rival: "Rivalry", other: "Other"
};

const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

interface Props { projectId: string; onOpenScene: (sceneId: string) => void; }
type RelTab = "all" | "suggestions" | "byCharacter" | "byType" | "changes" | "matrix";
interface CharInfo { name: string; imageData: string | null; }
interface DimRow {
  key: string; fromId: string; toId: string; fromName: string; toName: string;
  kind: RelationshipKind; status: string; description: string;
  chapters: number[]; events: RelationshipEventView[]; currentEventId: string;
}

/**
 * Relationships — how characters stand with each other and when it changes. A
 * portrait-first, tabbed view: every pair/dimension's current state with the
 * chapters it moves through, Scribe suggestions to approve, and groupings by
 * character / type, a changes log, and a matrix. Data comes from the relationship
 * event log (the latest event per pair+dimension is the current state).
 */
export function RelationshipsPanel({ projectId, onOpenScene }: Props) {
  const toast = useToast();
  const { pickModel } = useModelPicker();
  const [view, setView] = useState<RelationshipsView | null>(null);
  const [charMap, setCharMap] = useState<Map<string, CharInfo>>(new Map());
  const [sceneChapter, setSceneChapter] = useState<Map<string, number>>(new Map());
  const [tab, setTab] = useState<RelTab>("all");
  const [query, setQuery] = useState("");
  const [detecting, setDetecting] = useState(false);
  const [editing, setEditing] = useState<RelationshipEventView | "new" | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const reload = useCallback(async () => {
    try {
      const [v, chars, m] = await Promise.all([
        api.relationships(projectId),
        api.listCollection(projectId, "characters"),
        api.manuscript(projectId)
      ]);
      setView(v);
      setCharMap(new Map(chars.items.map((c) => [c.id, { name: String(c.name ?? ""), imageData: (c.imageData as string | null) ?? null }])));
      const chPos = new Map([...m.chapters].sort((a, b) => a.position - b.position).map((c, i) => [c.id, i + 1] as const));
      const sc = new Map<string, number>();
      for (const s of m.scenes) { const n = chPos.get(s.chapterId); if (n) sc.set(s.id, n); }
      setSceneChapter(sc);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load relationships");
    }
  }, [projectId, toast]);
  useEffect(() => { void reload(); }, [reload]);

  const suggestions = useMemo(() => (view?.events ?? []).filter((e) => !e.approved), [view]);

  const dimRows = useMemo<DimRow[]>(() => {
    const approved = (view?.events ?? []).filter((e) => e.approved);
    const groups = new Map<string, RelationshipEventView[]>();
    for (const e of approved) {
      const k = `${pairKey(e.fromId, e.toId)}::${e.kind}`;
      (groups.get(k) ?? groups.set(k, []).get(k)!).push(e);
    }
    const rows: DimRow[] = [];
    for (const [key, evs] of groups) {
      evs.sort((a, b) => a.order - b.order || a.createdAt.localeCompare(b.createdAt));
      const cur = evs[evs.length - 1]!;
      const chapters = [...new Set(evs.map((e) => (e.sceneId ? sceneChapter.get(e.sceneId) : undefined)).filter((n): n is number => typeof n === "number"))].sort((a, b) => a - b);
      rows.push({ key, fromId: cur.fromId, toId: cur.toId, fromName: cur.fromName, toName: cur.toName, kind: cur.kind, status: cur.status, description: cur.description ?? "", chapters, events: evs, currentEventId: cur.id });
    }
    rows.sort((a, b) => a.fromName.localeCompare(b.fromName) || a.toName.localeCompare(b.toName) || a.kind.localeCompare(b.kind));
    return rows;
  }, [view, sceneChapter]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? dimRows.filter((r) => `${r.fromName} ${r.toName} ${r.status} ${KIND_LABEL[r.kind]} ${r.description}`.toLowerCase().includes(q)) : dimRows;
  }, [dimRows, query]);

  const approve = async (id: string) => { try { setView(await api.approveRelationship(projectId, id)); } catch (e) { toast.error(e instanceof Error ? e.message : "Could not approve"); } };
  const approveAll = async () => { try { setView(await api.approveAllRelationships(projectId)); toast.success("All suggestions approved."); } catch (e) { toast.error(e instanceof Error ? e.message : "Could not approve all"); } };
  const dismissAll = async () => { try { setView(await api.dismissAllRelationships(projectId)); } catch (e) { toast.error(e instanceof Error ? e.message : "Could not dismiss all"); } };
  const remove = async (id: string) => { try { setView(await api.deleteRelationship(projectId, id)); } catch (e) { toast.error(e instanceof Error ? e.message : "Could not delete"); } };
  const detect = async () => {
    const choice = await pickModel({ task: "character-analysis" });
    if (!choice) return;
    setDetecting(true);
    try {
      const res = await api.detectRelationships(projectId, choice);
      setView(res);
      toast.success(res.result.created > 0 ? `Scribe proposed ${res.result.created} relationship ${res.result.created === 1 ? "change" : "changes"}.` : "Scribe found no new relationship changes.");
      if (res.result.created > 0) setTab("suggestions");
    } catch (e) { toast.error(e instanceof Error ? e.message : "Detection failed"); }
    finally { setDetecting(false); }
  };

  const avatar = (id: string, name: string) => {
    const info = charMap.get(id);
    return <CharacterAvatar name={info?.name || name} imageUrl={info?.imageData} size="sm" ring />;
  };
  const pairAvatars = (fromId: string, toId: string, fromName: string, toName: string) => (
    <span className="rel-pair-av">{avatar(fromId, fromName)}{avatar(toId, toName)}</span>
  );
  const chapterChips = (chapters: number[]) => {
    if (chapters.length === 0) return null;
    const shown = chapters.slice(0, 4);
    return (
      <div className="rel-chaps">
        {shown.map((n) => <span key={n} className="rel-chap">Ch {n}</span>)}
        {chapters.length > shown.length && <span className="rel-chap rel-chap-more">+{chapters.length - shown.length}</span>}
      </div>
    );
  };

  const findEvent = (id: string) => (view?.events ?? []).find((e) => e.id === id) ?? null;

  const dimRow = (r: DimRow) => {
    const isOpen = expanded.has(r.key);
    return (
      <div key={r.key} className="relrow-wrap">
        <div className="relrow">
          {pairAvatars(r.fromId, r.toId, r.fromName, r.toName)}
          <div className="relrow-main">
            <div className="relrow-line">
              <span className="relrow-names">{r.fromName} &amp; {r.toName}</span>
              <span className={`rel-kind rel-kind-${r.kind}`}>{KIND_LABEL[r.kind]}</span>
              <span className="relrow-status">{r.status}</span>
            </div>
            {r.description && <div className="relrow-desc">{r.description}</div>}
          </div>
          {chapterChips(r.chapters)}
          <button className="btn btn-icon btn-ghost btn-sm" title={isOpen ? "Hide progression" : "Show progression"} onClick={() => setExpanded((s) => { const n = new Set(s); n.has(r.key) ? n.delete(r.key) : n.add(r.key); return n; })}>
            <Icon name="history" size={15} />
          </button>
          <button className="btn btn-icon btn-ghost btn-sm" title="Edit" onClick={() => setEditing(findEvent(r.currentEventId) ?? "new")}><Icon name="edit" size={14} /></button>
          <button className="btn btn-icon btn-ghost btn-sm" title="Delete current state" onClick={() => void remove(r.currentEventId)}><Icon name="trash" size={14} /></button>
        </div>
        {isOpen && (
          <ol className="rel-progression">
            {r.events.map((e, i) => (
              <li key={e.id} className={i === r.events.length - 1 ? "is-current" : ""}>
                <span className="rel-dot" />
                <span className="relrow-status">{e.status}</span>
                <button className="rel-anchor-link" disabled={!e.sceneId} onClick={() => e.sceneId && onOpenScene(e.sceneId)}>
                  {e.sceneTitle ? `${e.chapterTitle} · ${e.sceneTitle}` : "Backstory"}
                </button>
                {e.source === "ai" && <span className="chip" style={{ fontSize: 9 }}>AI</span>}
                <span className="spacer" style={{ flex: 1 }} />
                <button className="btn btn-icon btn-ghost btn-sm" title="Edit" onClick={() => setEditing(e)}><Icon name="edit" size={13} /></button>
                <button className="btn btn-icon btn-ghost btn-sm" title="Delete" onClick={() => void remove(e.id)}><Icon name="trash" size={13} /></button>
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  };

  if (!view) return <div className="rel-loading"><Icon name="heart" size={24} className="spin" style={{ color: "var(--accent)" }} /></div>;
  const canAdd = view.characters.length >= 2;

  const TABS: { key: RelTab; label: string; count?: number }[] = [
    { key: "all", label: "All", count: dimRows.length },
    { key: "suggestions", label: "Scribe Suggestions", count: suggestions.length },
    { key: "byCharacter", label: "By Character" },
    { key: "byType", label: "By Type" },
    { key: "changes", label: "Changes" },
    { key: "matrix", label: "Matrix" }
  ];

  return (
    <div className="rel-page">
      <header className="rel-hero">
        <div className="rel-crumb"><Icon name="book" size={12} /> Story Bible <Icon name="chevronRight" size={11} /> Relationships</div>
        <div className="rel-hero-row">
          <div>
            <h1 className="rel-h1">Relationships</h1>
            <p className="rel-tagline">How characters stand with each other, and when it changes.</p>
          </div>
          <div className="row gap-2" style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
            <input className="input" placeholder="Search relationships…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 240 }} />
            <button className="btn btn-sm btn-ghost" onClick={() => void detect()} disabled={detecting || !canAdd}>
              <Icon name={detecting ? "clock" : "sparkles"} size={14} className={detecting ? "spin" : ""} /> {detecting ? "Detecting…" : "Detect"}
            </button>
            <button className="btn btn-sm btn-primary" onClick={() => setEditing("new")} disabled={!canAdd}><Icon name="plus" size={14} /> Add relationship</button>
          </div>
        </div>
        <div className="rel-tabs">
          {TABS.map((t) => (
            <button key={t.key} className={`rel-tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
              {t.key === "suggestions" && <Icon name="sparkles" size={13} />}
              {t.label}
              {typeof t.count === "number" && t.count > 0 && <span className="rel-tab-count">{t.count}</span>}
            </button>
          ))}
        </div>
      </header>

      {!canAdd ? (
        <div className="rel-empty"><Icon name="users" size={26} style={{ color: "var(--text-faint)" }} /><p className="muted">Add at least two characters first — relationships connect the people in your book.</p></div>
      ) : (
        <div className="rel-body">
          {tab === "suggestions" || (tab === "all" && suggestions.length > 0) ? (
            tab === "suggestions" && suggestions.length === 0 ? (
              <div className="rel-empty"><Icon name="sparkles" size={24} style={{ color: "var(--text-faint)" }} /><p className="muted">No suggestions right now. Use “Detect” to have Scribe propose relationship changes.</p></div>
            ) : (
              suggestions.length > 0 && (
                <section className="rel-suggest-card">
                  <div className="rel-suggest-head">
                    <Icon name="sparkles" size={16} style={{ color: "var(--accent)" }} />
                    <h3>Scribe suggestions</h3>
                    <span className="rel-tab-count">{suggestions.length}</span>
                    <span className="spacer" style={{ flex: 1 }} />
                    <button className="btn btn-sm" onClick={() => void approveAll()}><Icon name="check" size={13} /> Approve all</button>
                    <button className="btn btn-sm btn-ghost" onClick={() => void dismissAll()}><Icon name="x" size={13} /> Dismiss all</button>
                  </div>
                  <p className="faint" style={{ fontSize: 12, margin: "0 0 10px" }}>Review and approve to add to the timeline.</p>
                  {suggestions.map((e) => (
                    <div key={e.id} className="rel-suggest-row">
                      {pairAvatars(e.fromId, e.toId, e.fromName, e.toName)}
                      <div className="relrow-main">
                        <div className="relrow-line">
                          <span className="relrow-names">{e.fromName} &amp; {e.toName}</span>
                          <span className={`rel-kind rel-kind-${e.kind}`}>{KIND_LABEL[e.kind]}</span>
                          <span className="relrow-status">{e.status}</span>
                        </div>
                        <div className="relrow-desc">
                          {e.sceneTitle ? `${e.chapterTitle} · ${e.sceneTitle}` : "No scene anchor"}{e.description ? ` — ${e.description}` : ""}
                        </div>
                      </div>
                      <button className="btn btn-icon btn-ghost btn-sm" title="Approve" onClick={() => void approve(e.id)}><Icon name="check" size={16} style={{ color: "var(--success, #3fb950)" }} /></button>
                      <button className="btn btn-icon btn-ghost btn-sm" title="Dismiss" onClick={() => void remove(e.id)}><Icon name="x" size={16} /></button>
                    </div>
                  ))}
                </section>
              )
            )
          ) : null}

          {tab === "all" && (
            dimRows.length === 0 && suggestions.length === 0 ? (
              <div className="rel-empty"><Icon name="heart" size={26} style={{ color: "var(--text-faint)" }} /><p className="muted">No relationships yet. Add one, or let Scribe detect them.</p></div>
            ) : (
              <section>
                {dimRows.length > 0 && (
                  <div className="rel-list-head"><Icon name="thread" size={15} style={{ color: "var(--accent)" }} /><h3>Relationship history</h3><span className="faint" style={{ fontSize: 12 }}>progression by dimension over the story</span></div>
                )}
                <div className="rel-list">{filteredRows.map(dimRow)}</div>
              </section>
            )
          )}

          {tab === "byType" && KINDS.filter((k) => filteredRows.some((r) => r.kind === k)).map((k) => (
            <section key={k} style={{ marginBottom: 20 }}>
              <div className="rel-list-head"><span className={`rel-kind rel-kind-${k}`}>{KIND_LABEL[k]}</span></div>
              <div className="rel-list">{filteredRows.filter((r) => r.kind === k).map(dimRow)}</div>
            </section>
          ))}

          {tab === "byCharacter" && [...view.characters].sort((a, b) => a.name.localeCompare(b.name)).map((c) => {
            const rows = filteredRows.filter((r) => r.fromId === c.id || r.toId === c.id);
            if (rows.length === 0) return null;
            return (
              <section key={c.id} style={{ marginBottom: 20 }}>
                <div className="rel-list-head">{avatar(c.id, c.name)}<h3>{c.name}</h3><span className="faint" style={{ fontSize: 12 }}>{rows.length} relationship{rows.length === 1 ? "" : "s"}</span></div>
                <div className="rel-list">{rows.map(dimRow)}</div>
              </section>
            );
          })}

          {tab === "changes" && (
            <section>
              <div className="rel-list-head"><Icon name="timeline" size={15} style={{ color: "var(--accent)" }} /><h3>Changes</h3><span className="faint" style={{ fontSize: 12 }}>every state change in reading order</span></div>
              <ol className="rel-changes">
                {(view.events ?? []).filter((e) => e.approved).sort((a, b) => a.order - b.order || a.createdAt.localeCompare(b.createdAt)).map((e) => (
                  <li key={e.id} className="rel-change">
                    <span className="rel-chap" style={{ flex: "none" }}>{e.sceneId && sceneChapter.get(e.sceneId) ? `Ch ${sceneChapter.get(e.sceneId)}` : "—"}</span>
                    {pairAvatars(e.fromId, e.toId, e.fromName, e.toName)}
                    <span className="relrow-names">{e.fromName} &amp; {e.toName}</span>
                    <span className={`rel-kind rel-kind-${e.kind}`}>{KIND_LABEL[e.kind]}</span>
                    <span className="relrow-status">{e.status}</span>
                    <span className="spacer" style={{ flex: 1 }} />
                    {e.sceneId && <button className="rel-anchor-link" onClick={() => onOpenScene(e.sceneId!)}>{e.sceneTitle}</button>}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {tab === "matrix" && (
            <RelationshipMatrix characters={view.characters} rows={dimRows} avatar={avatar} />
          )}
        </div>
      )}

      {editing && (
        <RelationshipEditor
          projectId={projectId}
          view={view}
          event={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(v) => { setView(v); setEditing(null); void reload(); }}
        />
      )}
    </div>
  );
}

/** A compact character × character grid of current relationship dimensions. */
function RelationshipMatrix({ characters, rows, avatar }: {
  characters: { id: string; name: string }[];
  rows: DimRow[];
  avatar: (id: string, name: string) => ReactNode;
}) {
  const connected = characters.filter((c) => rows.some((r) => r.fromId === c.id || r.toId === c.id));
  if (connected.length === 0) return <div className="rel-empty"><Icon name="grip" size={24} style={{ color: "var(--text-faint)" }} /><p className="muted">No relationships to chart yet.</p></div>;
  const kindsFor = (a: string, b: string) => rows.filter((r) => (r.fromId === a && r.toId === b) || (r.fromId === b && r.toId === a));
  return (
    <div className="rel-matrix-wrap">
      <table className="rel-matrix">
        <thead>
          <tr>
            <th />
            {connected.map((c) => <th key={c.id} title={c.name}>{avatar(c.id, c.name)}</th>)}
          </tr>
        </thead>
        <tbody>
          {connected.map((row) => (
            <tr key={row.id}>
              <th className="rel-matrix-rowhead">{avatar(row.id, row.name)}<span className="truncate">{row.name}</span></th>
              {connected.map((col) => (
                <td key={col.id}>
                  {row.id === col.id ? <span className="faint">—</span> : (
                    <span className="rel-matrix-cell">
                      {kindsFor(row.id, col.id).map((r) => <span key={r.kind} className={`rel-dot-kind rel-kind-${r.kind}`} title={`${KIND_LABEL[r.kind]}: ${r.status}`} />)}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RelationshipEditor({ projectId, view, event, onClose, onSaved }: {
  projectId: string; view: RelationshipsView; event: RelationshipEventView | null;
  onClose: () => void; onSaved: (v: RelationshipsView) => void;
}) {
  const toast = useToast();
  const [fromId, setFromId] = useState(event?.fromId ?? view.characters[0]?.id ?? "");
  const [toId, setToId] = useState(event?.toId ?? view.characters[1]?.id ?? "");
  const [kind, setKind] = useState<RelationshipKind>(event?.kind ?? "friend");
  const [status, setStatus] = useState(event?.status ?? "");
  const [sceneId, setSceneId] = useState(event?.sceneId ?? "");
  const [description, setDescription] = useState(event?.description ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!status.trim()) { toast.error("Describe the state (e.g. “together”, “estranged”)."); return; }
    if (!event && fromId === toId) { toast.error("Pick two different characters."); return; }
    setSaving(true);
    try {
      const v = event
        ? await api.updateRelationship(projectId, event.id, { kind, status: status.trim(), description: description.trim() || null, sceneId: sceneId || null })
        : await api.createRelationship(projectId, { fromId, toId, kind, status: status.trim(), description: description.trim() || null, sceneId: sceneId || null });
      toast.success(event ? "Relationship updated." : "Relationship added.");
      onSaved(v);
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Modal
      title={event ? "Edit relationship" : "Add relationship"}
      subtitle="A point-in-time state between two characters, anchored where it happens."
      onClose={onClose} width={520}
      footer={<><button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : event ? "Save changes" : "Add"}</button></>}
    >
      {!event ? (
        <div className="row gap-3">
          <div className="grow field"><label className="label">Character</label><select className="select" value={fromId} onChange={(e) => setFromId(e.target.value)}>{view.characters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
          <div className="grow field"><label className="label">With</label><select className="select" value={toId} onChange={(e) => setToId(e.target.value)}>{view.characters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        </div>
      ) : (
        <div className="field"><label className="label">Between</label><div className="strong">{event.fromName} &amp; {event.toName}</div></div>
      )}
      <div className="row gap-3">
        <div className="grow field"><label className="label">Kind</label><select className="select" value={kind} onChange={(e) => setKind(e.target.value as RelationshipKind)}>{KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}</select></div>
        <div className="grow field"><label className="label">State</label><input className="input" value={status} onChange={(e) => setStatus(e.target.value)} placeholder="together, estranged, engaged…" /></div>
      </div>
      <div className="field"><label className="label">When it happens <span className="faint">(scene — optional)</span></label>
        <select className="select" value={sceneId} onChange={(e) => setSceneId(e.target.value)}>
          <option value="">Backstory / before the story</option>
          {view.scenes.map((s) => <option key={s.id} value={s.id}>{cap(s.chapterTitle)} · {s.title}</option>)}
        </select>
      </div>
      <div className="field"><label className="label">Note <span className="faint">(optional)</span></label>
        <textarea className="textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What changes, or any nuance." />
      </div>
    </Modal>
  );
}
